import {ProcessTaskStatusType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {accessibleObjs, getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRun} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {PredecessorEdge, resolveTasklessStatuses} from 'studies/typed/shared/taskStatusUtils';
import {AllPMEIsInStudyQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRunNode} from 'studies/typed/shared/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {
  AllPMEIsInStudyQuery,
  AllPMEIsInStudyQueryVariables,
} from 'studies/typed/shared/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {
  ALL_PMEIS_IN_STUDY_QUERY,
  getOrderedPMDVIds,
  getUnitOperationFields,
} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';

import {excludeArchivedPmeis, PLANNED} from '../procedureRunOverview';
import type {FlowchartVersionData} from './PMEIMaterialLinkUtils';
import {UnitOpLinkEdge} from './UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from './useUnitOpLinkingState';

export enum StudyProcedureRunFlowchartStateStatus {
  CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART = 'CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART',
  QUERY_ERROR = 'QUERY_ERROR',
  QUERY_PENDING = 'QUERY_PENDING',
  STUDY_INACCESSIBLE = 'STUDY_INACCESSIBLE',
  SUCCESS = 'SUCCESS',
}

export interface SuccessfulQueryStudyProcedureRunFlowchartState {
  status: StudyProcedureRunFlowchartStateStatus.SUCCESS;
  studyType: StudyType;
  pmeis: Array<UnitOperationFields>;
  intraProcedureLinks: Array<UnitOpLinkEdge>;
  interProcedureLinks: Array<UnitOpLinkEdge>;
  flowchartByProcedureRunId: Map<string, FlowchartVersionData>;
}

export interface ContainsExpandedFlowchartStudyProcedureRunFlowchartState {
  status: StudyProcedureRunFlowchartStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART;
  studyType: StudyType;
}

export interface QueryErrorStudyProcedureRunFlowchartState {
  status: Exclude<
    StudyProcedureRunFlowchartStateStatus,
    | StudyProcedureRunFlowchartStateStatus.SUCCESS
    | StudyProcedureRunFlowchartStateStatus.STUDY_INACCESSIBLE
    | StudyProcedureRunFlowchartStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART
  >;
  inaccessibleStudy?: never;
}

export interface StudyInaccessibleStudyProcedureRunFlowchartState {
  status: StudyProcedureRunFlowchartStateStatus.STUDY_INACCESSIBLE;
  inaccessibleStudy: {
    __typename: 'InaccessibleInfoNode';
    invalidId: string;
    canRequestAccess: boolean;
  };
}

export type NotSuccessfulQueryStudyProcedureRunFlowchartState =
  | QueryErrorStudyProcedureRunFlowchartState
  | StudyInaccessibleStudyProcedureRunFlowchartState
  | ContainsExpandedFlowchartStudyProcedureRunFlowchartState;

/**
 * Fetches data from the study for use by the StudyProcedureRunFlowchart.
 * Similar to useUnitOpLinkingState, but fetches all PMEIs and links in the study
 * and doesn't separate the PMEIs into source and destination.
 */
export default function useStudyProcedureRunFlowchartState(
  studyId: string
): SuccessfulQueryStudyProcedureRunFlowchartState | NotSuccessfulQueryStudyProcedureRunFlowchartState {
  const result = useBenchlingQuery<AllPMEIsInStudyQuery, AllPMEIsInStudyQueryVariables>(
    ALL_PMEIS_IN_STUDY_QUERY,
    {
      variables: {studyId},
      // renderCachedDataWhenRefetching must be false here to avoid use of incomplete data from other queries
      // after successful creation of a new run/experiment (at least until apollo-client is upgraded to 3.10+)
      // See issue "useQuery can unexpectedly return partial data"
      renderCachedDataWhenRefetching: false,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  let orderedPMEIs: Array<UnitOperationFields> = [];
  let procedureRuns: Array<ProcedureRunNode> = [];
  let renderedPMEIIds = new Set<string>();
  let flowchartByProcedureRunId = new Map<string, FlowchartVersionData>();
  if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    procedureRuns = accessibleObjs(result.data.study.procedureRuns);
    if (containsExpandedFlowchartProcedureRun(procedureRuns)) {
      return {
        studyType: result.data.study.studySchema.studyType,
        status: StudyProcedureRunFlowchartStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART,
      };
    }
    orderedPMEIs = getOrderedPMEIs(procedureRuns);
    renderedPMEIIds = new Set(orderedPMEIs.map((pmei) => pmei.pmeiId));
    flowchartByProcedureRunId = buildFlowchartMap(procedureRuns);
  }

  if (result.type === QueryResultType.ERROR) {
    return {
      status: StudyProcedureRunFlowchartStateStatus.QUERY_ERROR,
    };
  } else if (result.type === QueryResultType.PENDING) {
    return {
      status: StudyProcedureRunFlowchartStateStatus.QUERY_PENDING,
    };
  } else if (isInaccessible(result.data.study)) {
    return {
      status: StudyProcedureRunFlowchartStateStatus.STUDY_INACCESSIBLE,
      inaccessibleStudy: result.data.study,
    };
  } else {
    // Query was successful and study is accessible.
    return {
      status: StudyProcedureRunFlowchartStateStatus.SUCCESS,
      studyType: result.data.study.studySchema.studyType,
      pmeis: orderedPMEIs,
      intraProcedureLinks: getIntraProcedureRunLinks(
        accessibleObjs(result.data.study.procedureRuns),
        renderedPMEIIds
      ),
      interProcedureLinks: getInterProcedureRunLinks(
        accessibleObjs(result.data.study.procedureRuns),
        renderedPMEIIds
      ),
      flowchartByProcedureRunId,
    };
  }
}

function getOrderedPMEIs(procedureRuns: Array<ProcedureRunNode>): Array<UnitOperationFields> {
  return procedureRuns.flatMap((procedureRun, runIndex) => {
    const orderedPMDVIds = getOrderedPMDVIds(procedureRun);
    const resolvedStatuses = resolveStatusesForProcedureRun(procedureRun);
    return excludeArchivedPmeis(procedureRun.methodExecutionInstances).map((pmei, pmeiIndex) => {
      const status = resolvedStatuses.get(pmei.id) ?? PLANNED;
      return getUnitOperationFields({
        pmei,
        pmeiIndex,
        procedureRun,
        runIndex,
        orderedPMDVIds,
        status,
      });
    });
  });
}

function resolveStatusesForProcedureRun(
  procedureRun: ProcedureRunNode
): ReadonlyMap<string, ProcessTaskStatusType | null> {
  const statusMap = new Map<string, ProcessTaskStatusType | null>();
  const pmeiMethodIds = new Map<string, string>();

  for (const pmei of procedureRun.methodExecutionInstances) {
    const methodTask = pmei.methodTask;
    const status = methodTask == null || isInaccessible(methodTask) ? null : methodTask.status.statusType;
    statusMap.set(pmei.id, status);
    pmeiMethodIds.set(pmei.id, getAccessibleOrInaccessibleId(pmei.templateMethodVersion));
  }

  const predecessorEdges: Array<PredecessorEdge> = procedureRun.conditionReplicateFlowchart.map((edge) => ({
    sourcePmeiId: edge.sourceProcedureMethodExecutionInstance.id,
    targetPmeiId: edge.targetProcedureMethodExecutionInstance.id,
  }));

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  const methods = isInaccessible(flowchartVersion)
    ? []
    : flowchartVersion.nodes.map((node) => ({id: node.id}));

  return resolveTasklessStatuses(statusMap, predecessorEdges, methods, pmeiMethodIds);
}

/**
 * Returns true if any of the procedure runs have an expanded flowchart.
 * This is used to determine whether we show the StudyProcedureRunFlowchart.
 * Procedure runs with expanded flowcharts cause visualization errors because their
 * non-linear structure breaks the methodOrderKey calculation that relies on linear node ordering.
 */
function containsExpandedFlowchartProcedureRun(procedureRuns: Array<ProcedureRunNode>): boolean {
  return procedureRuns.some((procedureRun) => procedureRun.flowchartIsExpanded);
}

/**
 * Returns the links between PMEIs in different procedure runs.
 * There's no need to check if the PMEIs are in the same study because
 * we assume all unit op instance links are within the same study.
 *
 * @param procedureRuns - The procedure runs to get links for.
 * @returns The links between PMEIs in different procedure runs.
 */
function getInterProcedureRunLinks(
  procedureRuns: Array<ProcedureRunNode>,
  renderedPMEIIds: ReadonlySet<string>
): Array<UnitOpLinkEdge> {
  const links: Array<UnitOpLinkEdge> = [];
  procedureRuns.forEach((procedureRun) => {
    procedureRun.methodExecutionInstances.forEach((pmei) => {
      pmei.targetProcedureMethodExecutionInstanceAssociations.forEach((association) => {
        if (!isInaccessible(association)) {
          const sourcePmeiId = association.sourceProcedureMethodExecutionInstance.id;
          const targetPmeiId = association.targetProcedureMethodExecutionInstance.id;
          if (
            sourcePmeiId &&
            targetPmeiId &&
            renderedPMEIIds.has(sourcePmeiId) &&
            renderedPMEIIds.has(targetPmeiId)
          ) {
            links.push({sourcePMEIId: sourcePmeiId, destinationPMEIId: targetPmeiId});
          }
        }
      });
    });
  });
  return links;
}

function getIntraProcedureRunLinks(
  procedureRuns: Array<ProcedureRunNode>,
  renderedPMEIIds: ReadonlySet<string>
): Array<UnitOpLinkEdge> {
  return procedureRuns.flatMap((procedureRun) =>
    procedureRun.conditionReplicateFlowchart.flatMap((edge) => {
      const sourcePMEIId = edge.sourceProcedureMethodExecutionInstance.id;
      const destinationPMEIId = edge.targetProcedureMethodExecutionInstance.id;
      if (!renderedPMEIIds.has(sourcePMEIId) || !renderedPMEIIds.has(destinationPMEIId)) {
        return [];
      }
      return [{sourcePMEIId, destinationPMEIId}];
    })
  );
}

function buildFlowchartMap(procedureRuns: Array<ProcedureRunNode>): Map<string, FlowchartVersionData> {
  const map = new Map<string, FlowchartVersionData>();
  for (const run of procedureRuns) {
    const rawFlowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(run);
    if (isInaccessible(rawFlowchartVersion)) {
      continue;
    }
    map.set(run.id, {
      edges: rawFlowchartVersion.edges,
      nodes: rawFlowchartVersion.nodes,
    });
  }
  return map;
}
